import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import {
  gameSportcastId,
  gameSportcastIdWhere,
  sports
} from 'utils/constants/sportsbook/data-fields';
import { getMountPath } from 'utils/generic/route-manipulation';
import { SPORTSBOOK_ROUTE_WITH_CATEGORY } from 'utils/sportsbook/route.structure';
import { getLivePreMatchSportTypes } from 'utils/sportsbook/sport-data';
import RidGenerator from 'utils/swarm/rid-generator';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { executeCommand } from 'services/get-data-level';
import { unsubscribeCommand } from 'services/get-swarm-data';
import { SportItem, SportItemSkeleton } from './SportItem';
import { NoItem } from 'components/Shared/NoItem';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useMounted } from 'hooks/useMounted';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setGameInfo } from 'store/actions/game-data';
import { setShowNoItemWrapper } from 'store/actions/new-sportsbook';
import { getPrematchBoostedOddsIds } from 'store/selectors/sport-data';
import './index.less';

type TVerticalSportsListProps = {
  sportCategory: ENotRealSports;
  isTopMatches?: boolean;
  handleRedirect?: (url: string) => void;
};

export const VerticalSportsList = ({
  sportCategory
}: TVerticalSportsListProps): ReactElement => {
  const configs = useSportsbookConfigs();
  const boostedGameIDs = useSelector(getPrematchBoostedOddsIds);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { mounted } = useMounted();
  const history = useHistory();
  const routeParams = useSbRouteMatch(SPORTSBOOK_ROUTE_WITH_CATEGORY);

  const sportInRoute = routeParams?.params.sport;

  const [sportsData, setSportsData] = useState<Sport[] | null | undefined>(
    null
  );

  useEffect(() => {
    let shouldUnsubscribe = false;

    if (sportCategory === ENotRealSports.boostedOdds && !boostedGameIDs) {
      return;
    }

    shouldUnsubscribe = true;
    const where: Record<string, any> = {
      game: {
        type: {
          '@in': [0, 2]
        },
        id: {
          '@in': boostedGameIDs
        }
      }
    };

    if (configs.sportType) {
      where.sport = {
        type: {
          '@in': getLivePreMatchSportTypes(configs.sportType)
        }
      };
    }

    const what: {
      [key: string]: Array<string>;
    } = {
      sport: sports.sport
    };

    if (sportCategory === ENotRealSports.betBuilder) {
      delete where.game.id;

      where.game.sportcast_id = gameSportcastIdWhere;
      what.game = gameSportcastId;
    }

    const Rid = RidGenerator.gForSubscribe('VerticalSportsList');

    setSportsData([]);
    executeCommand(
      Rid,
      what,
      where,
      (resp: { sport: Record<string, Sport> }) => {
        if (mounted.current && resp.sport && Object.keys(resp.sport).length) {
          const orderedSports = sortByOrder(Object.values(resp.sport));
          setSportsData(orderedSports);
        } else {
          dispatch(setShowNoItemWrapper({ competitions: true, game: true }));
          setSportsData(undefined);
        }
      }
    );

    return () => {
      if (shouldUnsubscribe) {
        unsubscribeCommand(Rid);
      }
    };
  }, [boostedGameIDs, sportCategory]);

  useEffect(() => {
    if (sportsData && !sportInRoute) {
      handleSportSelect(sportsData[0]);
    }
  }, [sportsData, sportInRoute]);

  const handleSportSelect = (sport: Sport) => {
    if (sport?.alias === sportInRoute) {
      return;
    }

    if (routeParams) {
      const path = `${getMountPath(routeParams.path)}/${sportCategory}/${
        sport.alias
      }`;

      dispatch(setGameInfo(null));
      history.push(path);
    }
  };

  if (sportsData === undefined) {
    return (
      <NoItem
        title={t('sportsbook.noGame')}
        text={t('sportsbook.noGameText')}
        size="medium"
      />
    );
  }

  return (
    <div className="sportsListWrapper">
      {sportsData?.length ? (
        sportsData?.map(sport => (
          <SportItem
            key={sport.id}
            sport={sport}
            isActive={sport.alias === sportInRoute}
            onClick={handleSportSelect}
          />
        ))
      ) : (
        <SportItemSkeleton />
      )}
    </div>
  );
};
